import React, { useState, useEffect, useRef } from 'react'
import DOMPurify from 'dompurify'
import { useNavigate, useParams, Link, useSearchParams } from 'react-router-dom'
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import RefreshIcon from '@mui/icons-material/Refresh'
import { supabase } from '../../config/supabase'
import type { Undertaker, ServicesTemplateStructure } from '../../types'
import LogoUpload from '../../components/LogoUpload/LogoUpload'
import GoogleRatingInfo from '../../components/GoogleRatingInfo'
import BusinessReviewsList from '../../components/BusinessReviewsList'
import RichTextEditor from '../../components/RichTextEditor/RichTextEditor'
import MapDrawing from '../../components/MapDrawing/MapDrawing'
import ServicesFormRenderer from '../../components/ServicesFormRenderer/ServicesFormRenderer'
import { PrisberegnerForm, PrisberegnerFormHandle } from '../../components/PrisberegnerForm'
import { useAuth } from '../../hooks/useAuth'
import {
  geoJSONToServiceArea,
  serviceAreaToGeoJSON,
  createDefaultRadiusArea,
} from '../../utils/serviceAreaUtils'
import {
  validateCompanyFields,
  hasValidationErrors,
  normalizeCompanyFields,
  getPhoneError,
  getEmailError,
  getCVRError,
  getWebsiteError,
  getFacebookError,
  getInstagramError,
  getGoogleBusinessError,
  getTrustpilotError,
  type ValidationErrors,
} from '../../utils/validation'

// Simple type for chain dropdown (only need id and name)
interface ChainOption {
  id: string
  name: string
  slug: string
}

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  )
}

// Helper to check if boost_area has actual polygon data (vs being a placeholder {})
function hasValidBoostArea(boostArea: any): boolean {
  return boostArea && 
    boostArea.type === 'polygon' && 
    Array.isArray(boostArea.polygons) && 
    boostArea.polygons.length > 0
}

// Helper to check if boost_area exists (even as placeholder) - for showing UI
function hasBoostAreaEnabled(boostArea: any): boolean {
  return boostArea !== null && boostArea !== undefined && typeof boostArea === 'object'
}

export default function UndertakerForm() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isEdit = Boolean(id)
  const { isAdmin, user } = useAuth()
  
  // Get chainId from URL params (used when creating new location from chain page)
  const preselectedChainId = searchParams.get('chainId')

  // Access control state
  const [hasAccess, setHasAccess] = useState(true)
  const [checkingAccess, setCheckingAccess] = useState(false)

  const [activeTab, setActiveTab] = useState(0)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [geocoding, setGeocoding] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [geocodeMessage, setGeocodeMessage] = useState<string | null>(null)

  const [formData, setFormData] = useState<Partial<Undertaker>>({
    name: '',
    phone: '',
    email: '',
    website: '',
    cvr_number: '',
    contact_person_name: '',
    contact_person_email: '',
    address: '',
    city: '',
    postal_code: '',
    system_address: '',
    facebook_link: '',
    google_business_link: '',
    instagram: '',
    trustpilot: '',
    usp1: '',
    usp2: '',
    usp3: '',
    logo_url: '',
    description: '',
    default_radius: 30,
    global_boost: false,
    boost_priority: 0,
    prices: null,
    rating: undefined,
    chain_id: null,
    is_separate_entity: false,
    is_verified: false,
  })
  const [showSystemAddress, setShowSystemAddress] = useState(false)
  const [chainData, setChainData] = useState<any>(null)
  const [availableChains, setAvailableChains] = useState<ChainOption[]>([])
  
  // Ref for Prisberegner form to call save from parent
  const prisberegnerFormRef = useRef<PrisberegnerFormHandle>(null)
  const [loadingChains, setLoadingChains] = useState(false)
  const [servicesTemplate, setServicesTemplate] = useState<ServicesTemplateStructure | null>(null)
  const [servicesData, setServicesData] = useState<Record<string, any>>({})
  const [fetchingRating, setFetchingRating] = useState(false)
  const [ratingMessage, setRatingMessage] = useState<string | null>(null)
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({})
  
  // Check if profile fields should be disabled (part of chain, not separate entity)
  const isProfileDisabled = Boolean(formData.chain_id && !formData.is_separate_entity)

  // Fetch rating from Trustpilot and save it
  const fetchTrustpilotRating = async () => {
    if (!formData.trustpilot) {
      setRatingMessage('❌ Indtast først en Trustpilot URL')
      return
    }

    setFetchingRating(true)
    setRatingMessage(null)

    try {
      const { data, error } = await supabase.functions.invoke('fetch-trustpilot-rating', {
        body: { trustpilotUrl: formData.trustpilot }
      })

      if (error) throw error

      if (data.error) {
        setRatingMessage(`❌ ${data.error}`)
        return
      }

      if (data.rating !== undefined) {
        // Update local state
        setFormData(prev => ({ ...prev, rating: data.rating, review_count: data.reviewCount ?? null }))
        
        // Auto-save the rating and review count to database
        if (id) {
          const { error: saveError } = await supabase
            .from('undertakers')
            .update({ 
              rating: data.rating,
              review_count: data.reviewCount ?? null
            })
            .eq('id', id)

          if (saveError) {
            console.error('Error saving rating:', saveError)
            setRatingMessage(`⚠️ Rating hentet (${data.rating}) men kunne ikke gemmes: ${saveError.message}`)
            return
          }
        }

        const reviewText = data.reviewCount ? ` (${data.reviewCount} anmeldelser)` : ''
        setRatingMessage(`✅ Rating hentet og gemt: ${data.rating}${reviewText}`)
      }
    } catch (err: any) {
      console.error('Error fetching rating:', err)
      setRatingMessage(`❌ Fejl: ${err.message || 'Kunne ikke hente rating'}`)
    } finally {
      setFetchingRating(false)
    }
  }

  // Check user access when editing
  useEffect(() => {
    if (isEdit && id) {
      // Set checking state immediately for edit mode
      if (!isAdmin) {
        setCheckingAccess(true)
      }
      checkUserAccess()
      fetchUndertaker()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit, isAdmin])

  const checkUserAccess = async () => {
    if (!user || !id) {
      setCheckingAccess(false)
      return
    }

    // Admins always have access
    if (isAdmin) {
      setHasAccess(true)
      setCheckingAccess(false)
      return
    }

    try {
      // Check if user has access to this undertaker (direct claim or chain claim)
      const { data, error } = await supabase
        .rpc('user_has_undertaker_access', {
          p_user_id: user.id,
          p_undertaker_id: id,
        })

      if (error) {
        console.error('Error checking access:', error)
        setHasAccess(false)
      } else {
        setHasAccess(data === true)
      }
    } catch (err) {
      console.error('Error checking access:', err)
      setHasAccess(false)
    } finally {
      setCheckingAccess(false)
    }
  }

  // Fetch available chains for the dropdown (admin only)
  useEffect(() => {
    if (!isAdmin) return
    
    const fetchChains = async () => {
      setLoadingChains(true)
      try {
        const { data, error } = await supabase
          .from('chains')
          .select('id, name, slug')
          .order('name')

        if (error) throw error
        setAvailableChains(data || [])
      } catch (err) {
        console.error('Error fetching chains:', err)
      } finally {
        setLoadingChains(false)
      }
    }

    fetchChains()
  }, [isAdmin])

  // Set chain_id from URL param when creating new undertaker
  useEffect(() => {
    if (!isEdit && preselectedChainId && availableChains.length > 0) {
      const chain = availableChains.find(c => c.id === preselectedChainId)
      if (chain) {
        setFormData(prev => ({ ...prev, chain_id: preselectedChainId }))
        setChainData(chain)
      }
    }
  }, [isEdit, preselectedChainId, availableChains])

  const fetchUndertaker = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('undertakers')
        .select('*, chain:chains(*)')
        .eq('id', id)
        .single()

      if (error) throw error
      if (data) {
        setFormData(data)
        if (data.chain) {
          setChainData(data.chain)
        }
        if (data.system_address) {
          setShowSystemAddress(true)
        }
        // Load services data
        if (data.services_data?.data) {
          setServicesData(data.services_data.data)
        }
      }
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  // Fetch services template on mount
  useEffect(() => {
    const fetchServicesTemplate = async () => {
      try {
        const { data, error } = await supabase
          .from('services_templates')
          .select('*')
          .eq('is_active', true)
          .limit(1)

        if (error) throw error
        if (data && data.length > 0 && data[0].template) {
          setServicesTemplate(data[0].template)
        }
      } catch (err: any) {
        console.error('Error fetching services template:', err)
      }
    }

    fetchServicesTemplate()
  }, [])

  const handleChange = (field: string) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.value
    setFormData({ ...formData, [field]: value })
    
    // Real-time validation for specific fields
    const validationFn: Record<string, (v: string) => string | null> = {
      phone: getPhoneError,
      email: getEmailError,
      contact_person_email: getEmailError,
      cvr_number: getCVRError,
      website: getWebsiteError,
      facebook_link: getFacebookError,
      instagram: getInstagramError,
      google_business_link: getGoogleBusinessError,
      trustpilot: getTrustpilotError,
    }
    
    if (validationFn[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: validationFn[field](value)
      }))
    }
  }

  // Handle services data field changes
  const handleServicesDataChange = (fieldId: string, value: any) => {
    setServicesData(prev => ({
      ...prev,
      [fieldId]: value
    }))
  }

  // Auto-geocode address when fields change
  useEffect(() => {
    const { address, city, system_address } = formData
    
    // Use system_address if available, otherwise use regular address
    const addressForGeocoding = system_address || address
    
    // Only geocode if we have both address and city
    if (!addressForGeocoding || !city) {
      return
    }

    // Debounce: wait 1.5 seconds after user stops typing
    const timeoutId = setTimeout(async () => {
      setGeocoding(true)
      setGeocodeMessage(null)

      try {
        // Build full address
        const fullAddress = `${addressForGeocoding}, ${formData.postal_code || ''} ${city}, Danmark`.trim()
        
        // Use Nominatim (OpenStreetMap) for geocoding
        const url = `https://nominatim.openstreetmap.org/search?` + new URLSearchParams({
          q: fullAddress,
          format: 'json',
          limit: '1',
          countrycodes: 'dk',
        }).toString()

        const response = await fetch(url, {
          headers: {
            'User-Agent': 'FindBedemand/1.0',
          },
        })

        const data = await response.json()

        if (data && data.length > 0) {
          const { lat, lon } = data[0]
          setFormData((prev) => ({
            ...prev,
            location: {
              lat: parseFloat(lat),
              lng: parseFloat(lon),
            },
          }))
          setGeocodeMessage(`✅ Koordinater fundet automatisk${system_address ? ' (via system-adresse)' : ''}`)
        } else {
          setGeocodeMessage('⚠️ Kunne ikke finde adressen. Tjek stavning.')
        }
      } catch (err: any) {
        setGeocodeMessage(`❌ Fejl: ${err.message}`)
      } finally {
        setGeocoding(false)
      }
    }, 1500) // Wait 1.5 seconds after user stops typing

    // Cleanup timeout if component unmounts or fields change again
    return () => clearTimeout(timeoutId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.address, formData.city, formData.postal_code, formData.system_address])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate all fields before saving
    const errors = validateCompanyFields(formData)
    setValidationErrors(errors)
    
    if (hasValidationErrors(errors)) {
      setError('Ret venligst fejlene i formularen før du gemmer.')
      // Scroll to top to show error
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
    
    setSaving(true)
    setError(null)
    setSuccess(false)

    try {
      // Prepare data for save - exclude relation properties that shouldn't be saved
      const { chain, ...dataWithoutRelations } = formData as any
      
      // Normalize fields before saving
      const normalizedData = normalizeCompanyFields(dataWithoutRelations)
      let dataToSave: any = { ...dataWithoutRelations, ...normalizedData }
      
      // Clean up empty boost_area (convert placeholder {} to null)
      if (!hasValidBoostArea(dataToSave.boost_area)) {
        dataToSave.boost_area = null
      }
      
      // If no service_area but has location, create default radius
      if (!dataToSave.service_area && dataToSave.location) {
        dataToSave.service_area = createDefaultRadiusArea(
          dataToSave.location,
          dataToSave.default_radius || 30
        )
      }

      // Add services_data if undertaker can have own services
      // (standalone or separate entity)
      if (!formData.chain_id || formData.is_separate_entity) {
        dataToSave.services_data = {
          last_updated: new Date().toISOString(),
          data: servicesData
        }
      }
      
      if (isEdit) {
        const { error } = await supabase
          .from('undertakers')
          .update(dataToSave)
          .eq('id', id)

        if (error) throw error
      } else {
        // New undertakers start as unpublished (unless created by admin)
        const dataToInsert = {
          ...dataToSave,
          published: isAdmin, // Auto-publish if admin creates it
          published_at: isAdmin ? new Date().toISOString() : null,
        }
        
        const { data: newUndertaker, error } = await supabase
          .from('undertakers')
          .insert([dataToInsert])
          .select('id')
          .single()

        if (error) throw error
        
        // Redirect to edit page with new ID so subsequent saves UPDATE instead of INSERT
        if (newUndertaker?.id) {
          navigate(`/undertakers/edit/${newUndertaker.id}`, { replace: true })
          return // Exit early since we're navigating
        }
      }

      // Save Prisberegner data if enabled
      if (formData.prisberegner_enabled && prisberegnerFormRef.current) {
        const prisberegnerSaved = await prisberegnerFormRef.current.save()
        if (!prisberegnerSaved) {
          setError('Bedemand gemt, men kunne ikke gemme prisberegner data. Prøv igen.')
          return
        }
      }

      setSuccess(true)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  if (loading || checkingAccess) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  // Non-admins cannot create new undertakers - they must use claims flow
  if (!isEdit && !isAdmin) {
    return (
      <Box>
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/claims')}
            sx={{ minWidth: 'auto' }}
          >
            Tilbage
          </Button>
        </Box>
        <Alert severity="warning">
          <Typography variant="h6" sx={{ mb: 1 }}>
            Opret via ansøgning
          </Typography>
          <Typography variant="body2">
            For at administrere en bedemand skal du gå til "Mine forretninger" og ansøge om at tilknytte en eksisterende virksomhed.
            Kontakt administrator hvis din virksomhed ikke findes i systemet.
          </Typography>
        </Alert>
      </Box>
    )
  }

  if (isEdit && !hasAccess) {
    return (
      <Box>
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/claims')}
            sx={{ minWidth: 'auto' }}
          >
            Tilbage
          </Button>
        </Box>
        <Alert severity="error">
          <Typography variant="h6" sx={{ mb: 1 }}>
            Ingen adgang
          </Typography>
          <Typography variant="body2">
            Du har ikke adgang til at redigere denne bedemand. 
            For at få adgang skal du først ansøge om at tilknytte virksomheden via "Mine forretninger" og afvente godkendelse.
          </Typography>
        </Alert>
      </Box>
    )
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/undertakers')}
          sx={{ minWidth: 'auto' }}
        >
          Tilbage
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          {isEdit ? 'Rediger bedemand' : 'Opret ny bedemand'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          ✓ Gemt succesfuldt!
        </Alert>
      )}

      {/* Chain context banner */}
      {chainData && (
        <Alert 
          severity="info" 
          sx={{ mb: 3 }}
          action={
            <Button 
              color="inherit" 
              size="small"
              onClick={() => navigate(`/chains/edit/${chainData.id}`)}
            >
              Gå til kæde
            </Button>
          }
        >
          <strong>🔗 Del af kæde: {chainData.name}</strong>
          <br />
          {!formData.is_separate_entity && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Denne lokation bruger kædens profildata. Kun adresse og serviceområde kan redigeres her.
            </Typography>
          )}
          {formData.is_separate_entity && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Denne lokation har separat profil og kan redigeres frit.
            </Typography>
          )}
        </Alert>
      )}

      {/* Warning if missing coordinates */}
      {isEdit && (!formData.location || !formData.location.lat || !formData.location.lng) && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <strong>⚠️ Mangler koordinater!</strong> 
          <br />
          Denne bedemand vises ikke korrekt på kortet. Gå til "Grundlæggende info"-fanen og udfyld adresse, by og postnummer for at få automatisk geocoding.
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Card>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider', px: 3, pt: 2 }}
          >
            <Tab label="Grundlæggende info" />
            <Tab label="Sociale medier" />
            <Tab label="Marketing" />
            <Tab label="Om os" />
            <Tab label="Serviceområde" />
            <Tab label="Services" />
            {(isAdmin || formData.prisberegner_enabled) && <Tab label="Prisberegner" />}
            {isAdmin && <Tab label="Boost" />}
          </Tabs>

          <CardContent sx={{ p: 4 }}>
            {/* Tab 1: Basic Info */}
            <TabPanel value={activeTab} index={0}>
              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Virksomhedsoplysninger
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Box sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    required
                    label="Virksomhedsnavn"
                    value={formData.name || ''}
                    onChange={handleChange('name')}
                  />
                </Box>

                {/* Verification Toggle - Admin only */}
                {isAdmin && (
                  <Box sx={{ mb: 3, p: 2, bgcolor: 'success.50', borderRadius: 2, border: '1px solid', borderColor: 'success.100' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.is_verified || false}
                          onChange={(e) => setFormData({ ...formData, is_verified: e.target.checked })}
                          color="success"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.900' }}>
                            ✓ Profil er verificeret
                          </Typography>
                          <Typography variant="caption" color="success.800">
                            Marker denne hvis profilen er manuelt udfyldt og valideret. Viser et grønt badge på hjemmesiden.
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>
                )}

                {/* Chain selector - admin only */}
                {isAdmin && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
                      Kædetilknytning
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <FormControl fullWidth>
                      <InputLabel id="chain-select-label">Tilhører kæde</InputLabel>
                      <Select
                        labelId="chain-select-label"
                        value={formData.chain_id || ''}
                        label="Tilhører kæde"
                        onChange={(e) => {
                          const newChainId = e.target.value || null
                          setFormData({ ...formData, chain_id: newChainId })
                          if (newChainId) {
                            const chain = availableChains.find(c => c.id === newChainId)
                            setChainData(chain || null)
                          } else {
                            setChainData(null)
                          }
                        }}
                        disabled={loadingChains}
                      >
                        <MenuItem value="">
                          <em>Ingen (selvstændig bedemand)</em>
                        </MenuItem>
                        {availableChains.map((chain) => (
                          <MenuItem key={chain.id} value={chain.id}>
                            {chain.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    
                    {formData.chain_id && (
                      <Box sx={{ mt: 2 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.is_separate_entity || false}
                              onChange={(e) => setFormData({ ...formData, is_separate_entity: e.target.checked })}
                            />
                          }
                          label="Separat profil (lokationen har egen beskrivelse, kontaktinfo og services)"
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                          {formData.is_separate_entity 
                            ? 'Denne lokation kan have egne data uafhængigt af kæden.'
                            : 'Denne lokation arver profildata fra kæden.'}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}

                {/* External contact info */}
                <Alert severity="info" sx={{ mb: 2, mt: 3 }}>
                  <strong>🌐 Vises eksternt på hjemmesiden</strong>
                </Alert>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                  <Box sx={{ flex: '1 1 300px' }}>
                    <TextField
                      fullWidth
                      label="Telefon"
                      value={formData.phone || ''}
                      onChange={handleChange('phone')}
                      disabled={isProfileDisabled}
                      error={!!validationErrors.phone}
                      helperText={validationErrors.phone || (isProfileDisabled ? 'Redigeres på kædeniveau' : 'F.eks. 12 34 56 78')}
                      placeholder="12 34 56 78"
                    />
                  </Box>

                  <Box sx={{ flex: '1 1 300px' }}>
                    <TextField
                      fullWidth
                      type="email"
                      label="Email"
                      value={formData.email || ''}
                      onChange={handleChange('email')}
                      disabled={isProfileDisabled}
                      error={!!validationErrors.email}
                      helperText={validationErrors.email || (isProfileDisabled ? 'Redigeres på kædeniveau' : 'Vises på hjemmesiden')}
                    />
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                  <Box sx={{ flex: '1 1 300px' }}>
                    <TextField
                      fullWidth
                      label="Hjemmeside"
                      value={formData.website || ''}
                      onChange={handleChange('website')}
                      placeholder="https://www.example.dk"
                      disabled={isProfileDisabled}
                      error={!!validationErrors.website}
                      helperText={validationErrors.website || (isProfileDisabled ? 'Redigeres på kædeniveau' : 'Vises på hjemmesiden')}
                    />
                  </Box>

                  <Box sx={{ flex: '1 1 300px' }}>
                    <TextField
                      fullWidth
                      label="CVR-nummer"
                      value={formData.cvr_number || ''}
                      onChange={handleChange('cvr_number')}
                      placeholder="12345678"
                      disabled={isProfileDisabled}
                      error={!!validationErrors.cvr_number}
                      helperText={validationErrors.cvr_number || (isProfileDisabled ? 'Redigeres på kædeniveau' : '8 cifre')}
                    />
                  </Box>
                </Box>

                {/* Internal contact info */}
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 4 }}>
                  Kontaktperson (intern)
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Alert severity="warning" sx={{ mb: 2 }}>
                  <strong>🔒 Kun til internt brug - vises IKKE eksternt</strong>
                </Alert>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                  <Box sx={{ flex: '1 1 300px' }}>
                    <TextField
                      fullWidth
                      label="Kontaktperson navn"
                      value={formData.contact_person_name || ''}
                      onChange={handleChange('contact_person_name')}
                      placeholder="Fornavn Efternavn"
                      disabled={isProfileDisabled}
                      helperText={isProfileDisabled ? 'Redigeres på kædeniveau' : 'Kun til intern brug'}
                    />
                  </Box>

                  <Box sx={{ flex: '1 1 300px' }}>
                    <TextField
                      fullWidth
                      type="email"
                      label="Kontaktperson email"
                      value={formData.contact_person_email || ''}
                      onChange={handleChange('contact_person_email')}
                      placeholder="kontakt@firma.dk"
                      disabled={isProfileDisabled}
                      error={!!validationErrors.contact_person_email}
                      helperText={validationErrors.contact_person_email || (isProfileDisabled ? 'Redigeres på kædeniveau' : 'Kun til intern brug')}
                    />
                  </Box>
                </Box>

                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 4 }}>
                  Adresse
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Box sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    required
                    label="Adresse"
                    value={formData.address || ''}
                    onChange={handleChange('address')}
                    placeholder="Vejnavn og nummer"
                  />
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '2 1 300px' }}>
                    <TextField
                      fullWidth
                      required
                      label="By"
                      value={formData.city || ''}
                      onChange={handleChange('city')}
                    />
                  </Box>

                  <Box sx={{ flex: '1 1 150px' }}>
                    <TextField
                      fullWidth
                      label="Postnummer"
                      value={formData.postal_code || ''}
                      onChange={handleChange('postal_code')}
                    />
                  </Box>
                </Box>

                {/* System Address Toggle */}
                <Box sx={{ mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showSystemAddress}
                        onChange={(e) => {
                          setShowSystemAddress(e.target.checked)
                          if (!e.target.checked) {
                            setFormData({ ...formData, system_address: '' })
                          }
                        }}
                      />
                    }
                    label="Brug alternativ adresse til koordinater (hvis hovedadresse ikke virker)"
                  />
                </Box>

                {showSystemAddress && (
                  <Box sx={{ mt: 2 }}>
                    <TextField
                      fullWidth
                      label="System-adresse (til geocoding)"
                      value={formData.system_address || ''}
                      onChange={handleChange('system_address')}
                      placeholder="Forenklet adresse uden etage/side"
                      helperText="Denne adresse bruges KUN til at finde koordinater. Den vises IKKE til brugere."
                    />
                  </Box>
                )}

                {geocoding && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    🔍 Finder koordinater automatisk...
                  </Alert>
                )}
                
                {geocodeMessage && !geocoding && (
                  <Alert 
                    severity={geocodeMessage.includes('✅') ? 'success' : geocodeMessage.includes('⚠️') ? 'warning' : 'error'} 
                    sx={{ mt: 2 }}
                  >
                    {geocodeMessage}
                  </Alert>
                )}
                
                {formData.location && !geocoding && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    <strong>📍 Koordinater:</strong> {formData.location.lat.toFixed(4)}, {formData.location.lng.toFixed(4)}
                    <br />
                    <Typography variant="caption" color="text.secondary">
                      Gå til "Serviceområde"-fanen for at se adressen på kortet med 30km radius
                    </Typography>
                  </Alert>
                )}
              </Box>
            </TabPanel>

            {/* Tab 2: Social Media */}
            <TabPanel value={activeTab} index={1}>
              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Sociale medier & online tilstedeværelse
                </Typography>
                <Divider sx={{ mb: 3 }} />
                
                {isProfileDisabled && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Sociale medier redigeres på kædeniveau. Kun administratoren kan ændre disse felter.
                  </Alert>
                )}

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                  <Box sx={{ flex: '1 1 300px' }}>
                    <TextField
                      fullWidth
                      label="Facebook Link"
                      value={formData.facebook_link || ''}
                      onChange={handleChange('facebook_link')}
                      placeholder="https://facebook.com/jeresfirma"
                      disabled={isProfileDisabled}
                      error={!!validationErrors.facebook_link}
                      helperText={validationErrors.facebook_link || 'Link til jeres Facebook side'}
                    />
                  </Box>

                  <Box sx={{ flex: '1 1 300px' }}>
                    <TextField
                      fullWidth
                      label="Instagram"
                      value={formData.instagram || ''}
                      onChange={handleChange('instagram')}
                      placeholder="@brugernavn"
                      disabled={isProfileDisabled}
                      error={!!validationErrors.instagram}
                      helperText={validationErrors.instagram || '@brugernavn eller https://instagram.com/brugernavn'}
                    />
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                  <Box sx={{ flex: '1 1 300px' }}>
                    <TextField
                      fullWidth
                      label="Google Business Link"
                      value={formData.google_business_link || ''}
                      onChange={handleChange('google_business_link')}
                      placeholder="https://g.page/jeresfirma"
                      disabled={isProfileDisabled}
                      error={!!validationErrors.google_business_link}
                      helperText={validationErrors.google_business_link || 'Link fra Google Maps'}
                    />
                  </Box>

                  <Box sx={{ flex: '1 1 300px' }}>
                    <TextField
                      fullWidth
                      label="Trustpilot"
                      value={formData.trustpilot || ''}
                      onChange={handleChange('trustpilot')}
                      placeholder="https://dk.trustpilot.com/review/jeresfirma"
                      disabled={isProfileDisabled}
                      error={!!validationErrors.trustpilot}
                      helperText={validationErrors.trustpilot || 'Link til jeres Trustpilot profil'}
                    />
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: '1 1 300px' }}>
                    <TextField
                      fullWidth
                      label="Rating (0-5)"
                      type="number"
                      value={formData.rating || ''}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value)
                        if (!isNaN(value) && value >= 0 && value <= 5) {
                          setFormData({ ...formData, rating: value })
                        } else if (e.target.value === '') {
                          setFormData({ ...formData, rating: undefined })
                        }
                      }}
                      disabled={!isAdmin}
                      placeholder="0.0 - 5.0"
                      inputProps={{ min: 0, max: 5, step: 0.1 }}
                      helperText={isAdmin ? "Kun administratorer kan redigere rating" : "Rating kan kun redigeres af administratorer"}
                      sx={{
                        '& .MuiInputBase-input.Mui-disabled': {
                          color: 'text.secondary',
                          WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
                        },
                      }}
                    />
                  </Box>

                  <Box sx={{ flex: '1 1 300px', pt: 1 }}>
                    <Button
                      variant="outlined"
                      onClick={fetchTrustpilotRating}
                      disabled={fetchingRating || !formData.trustpilot || isProfileDisabled}
                      startIcon={fetchingRating ? <CircularProgress size={20} /> : <RefreshIcon />}
                    >
                      {fetchingRating ? 'Henter...' : 'Hent rating fra Trustpilot'}
                    </Button>
                  </Box>
                </Box>

                {ratingMessage && (
                  <Alert
                    severity={ratingMessage.includes('✅') ? 'success' : 'error'}
                    sx={{ mt: 2 }}
                    onClose={() => setRatingMessage(null)}
                  >
                    {ratingMessage}
                  </Alert>
                )}

                <GoogleRatingInfo
                  rating={formData.google_rating}
                  reviewCount={formData.google_review_count}
                  placeId={formData.google_place_id}
                  updatedAt={formData.google_rating_updated_at}
                />

                {/* Moderation flags are admin-only in RLS */}
                {isAdmin && isEdit && <BusinessReviewsList undertakerId={id} />}
              </Box>
            </TabPanel>

            {/* Tab 3: Marketing */}
            <TabPanel value={activeTab} index={2}>
              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Unikke Salgsforslag (USP)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Hvad adskiller jer fra andre bedemænd?
                </Typography>
                <Divider sx={{ mb: 3 }} />
                
                {isProfileDisabled && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Marketing-indhold redigeres på kædeniveau. Kun administratoren kan ændre disse felter.
                  </Alert>
                )}

                <Box sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    label="USP 1"
                    value={formData.usp1 || ''}
                    onChange={handleChange('usp1')}
                    placeholder="F.eks. 'Lang erfaring'"
                    disabled={isProfileDisabled}
                    inputProps={{ maxLength: 30 }}
                    helperText={`${(formData.usp1 || '').length}/30 tegn`}
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    label="USP 2"
                    value={formData.usp2 || ''}
                    onChange={handleChange('usp2')}
                    placeholder="F.eks. 'Personlig service'"
                    disabled={isProfileDisabled}
                    inputProps={{ maxLength: 30 }}
                    helperText={`${(formData.usp2 || '').length}/30 tegn`}
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    label="USP 3"
                    value={formData.usp3 || ''}
                    onChange={handleChange('usp3')}
                    placeholder="F.eks. 'Døgnservice'"
                    disabled={isProfileDisabled}
                    inputProps={{ maxLength: 30 }}
                    helperText={`${(formData.usp3 || '').length}/30 tegn`}
                  />
                </Box>

                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 4 }}>
                  Logo
                </Typography>
                <Divider sx={{ mb: 3 }} />

                {isProfileDisabled ? (
                  <Alert severity="info">
                    Logo redigeres på kædeniveau. Kun administratoren kan ændre logoet.
                  </Alert>
                ) : (
                <LogoUpload
                  currentLogoUrl={formData.logo_url}
                  onLogoChange={(url) => setFormData({ ...formData, logo_url: url })}
                />
                )}
              </Box>
            </TabPanel>

            {/* Tab 4: About */}
            <TabPanel value={activeTab} index={3}>
              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Om Virksomheden
                </Typography>
                <Divider sx={{ mb: 3 }} />
                
                {isProfileDisabled && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Beskrivelse redigeres på kædeniveau. Kun administratoren kan ændre beskrivelsen.
                  </Alert>
                )}

                <Box>
                  <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
                    Beskrivelse
                  </Typography>
                  {isProfileDisabled ? (
                    <Box 
                      sx={{ 
                        border: '1px solid #ddd', 
                        borderRadius: 1, 
                        p: 2, 
                        minHeight: 300,
                        backgroundColor: '#f5f5f5'
                      }}
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formData.description || '<p>Ingen beskrivelse</p>') }}
                    />
                  ) : (
                  <RichTextEditor
                    value={formData.description || ''}
                    onChange={(html) => setFormData({ ...formData, description: html })}
                    placeholder="Fortæl om virksomheden, jeres historie, værdier og hvad I tilbyder..."
                    minHeight={300}
                  />
                  )}
                </Box>
              </Box>
            </TabPanel>

            {/* Tab 5: Service Area */}
            <TabPanel value={activeTab} index={4}>
              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Serviceområde
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Definer hvor I tilbyder jeres services. Som standard dækker virksomheden{' '}
                  {formData.default_radius || 30} km radius fra adressen. Tegn et polygon på kortet for at
                  definere et brugerdefineret serviceområde.
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <MapDrawing
                  address={formData.address}
                  location={formData.location}
                  defaultRadius={formData.default_radius || 30}
                  serviceArea={serviceAreaToGeoJSON(formData.service_area)}
                  onServiceAreaChange={(geoJSON) => {
                    // Convert GeoJSON from map to custom format with bounds
                    const serviceArea = geoJSON ? geoJSONToServiceArea(geoJSON) : null
                    setFormData({ ...formData, service_area: serviceArea })
                  }}
                />
              </Box>
            </TabPanel>

            {/* Tab 6: Services */}
            <TabPanel value={activeTab} index={5}>
              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Services & Priser
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Udfyld information om jeres services, priser og tilbud.
                </Typography>
                <Divider sx={{ mb: 3 }} />

                {/* Show message if undertaker is part of chain and not separate entity */}
                {formData.chain_id && !formData.is_separate_entity && chainData ? (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Denne lokation bruger kædens services. 
                    <Link 
                      to={`/chains/edit/${formData.chain_id}`}
                      style={{ marginLeft: 8 }}
                    >
                      Rediger kæde-services →
                    </Link>
                  </Alert>
                ) : null}

                {servicesTemplate ? (
                  <ServicesFormRenderer
                    template={servicesTemplate}
                    data={servicesData}
                    onChange={handleServicesDataChange}
                    readOnly={Boolean(formData.chain_id && !formData.is_separate_entity)}
                  />
                ) : (
                  <Alert severity="warning">
                    Ingen services-skabelon fundet. Kontakt administrator for at oprette en.
                  </Alert>
                )}
              </Box>
            </TabPanel>

            {/* Tab 7: Prisberegner (Admin or if enabled) */}
            {(isAdmin || formData.prisberegner_enabled) && (
              <TabPanel value={activeTab} index={6}>
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Prisberegner
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Indstil priser for prisberegneren. Disse priser er adskilt fra "Services" fanen.
                  </Typography>
                  <Divider sx={{ mb: 3 }} />

                  {/* Enable toggle (admin only) */}
                  {isAdmin && (
                    <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.prisberegner_enabled || false}
                            onChange={(e) => setFormData({ ...formData, prisberegner_enabled: e.target.checked })}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              Aktivér Prisberegner
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Når aktiveret, kan bedemanden udfylde prisberegner-priser
                            </Typography>
                          </Box>
                        }
                      />
                    </Box>
                  )}

                  {/* Show message if undertaker is part of chain */}
                  {formData.chain_id && !formData.is_separate_entity && chainData ? (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Denne lokation bruger kædens prisberegner-indstillinger. 
                      <Link 
                        to={`/chains/edit/${chainData.id}`}
                        style={{ marginLeft: 8 }}
                      >
                        Rediger kæde-priser →
                      </Link>
                    </Alert>
                  ) : formData.prisberegner_enabled ? (
                    <PrisberegnerForm
                      ref={prisberegnerFormRef}
                      undertakerId={id}
                      readOnly={Boolean(formData.chain_id && !formData.is_separate_entity)}
                      isAdmin={isAdmin}
                    />
                  ) : (
                    <Alert severity="warning">
                      Prisberegneren er ikke aktiveret. {isAdmin ? 'Aktivér den ovenfor.' : 'Kontakt administrator for at aktivere.'}
                    </Alert>
                  )}
                </Box>
              </TabPanel>
            )}

            {/* Tab 8: Boost Management (Admin Only) */}
            {isAdmin && (
              <TabPanel value={activeTab} index={7}>
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Boost management
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Administrer hvordan denne bedemand fremhæves på sitet.
                  </Typography>
                  <Divider sx={{ mb: 3 }} />

                  {/* Global Boost Toggle */}
                  <Box sx={{ mb: 3, p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.global_boost || false}
                          onChange={(e) => setFormData({ ...formData, global_boost: e.target.checked })}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            Aktivér Global Boost
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Viser denne bedemand højere i søgeresultater inden for boost-området
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>

                  {/* Boost Priority */}
                  <Box sx={{ mb: 4 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Boost Prioritet"
                      value={formData.boost_priority || 0}
                      onChange={(e) => setFormData({ ...formData, boost_priority: parseInt(e.target.value) || 0 })}
                      helperText="Højere tal = højere prioritet (0 = normal, 1-10 = boostet, 10+ = meget boostet)"
                      disabled={!formData.global_boost}
                      inputProps={{ min: 0, max: 100 }}
                    />
                  </Box>

                  {formData.global_boost && (
                    <>
                      <Divider sx={{ my: 3 }} />
                      
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                        Boost Område
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Vælg hvor boost skal gælde. Boost-området kan maksimalt være lige så stort som serviceområdet.
                      </Typography>

                      <RadioGroup
                        value={hasBoostAreaEnabled(formData.boost_area) ? 'custom' : 'service_area'}
                        onChange={(e) => {
                          if (e.target.value === 'service_area') {
                            // Clear boost_area to use service_area
                            setFormData({ ...formData, boost_area: null })
                          } else if (e.target.value === 'custom') {
                            // Initialize boost_area with a placeholder to enable custom drawing
                            setFormData({ ...formData, boost_area: {} })
                          }
                        }}
                        sx={{ mb: 3 }}
                      >
                        <FormControlLabel
                          value="service_area"
                          control={<Radio />}
                          label={
                            <Box>
                              <Typography variant="body1">Brug serviceområde (standard)</Typography>
                              <Typography variant="caption" color="text.secondary">
                                Boost gælder i hele det område hvor I tilbyder services
                              </Typography>
                            </Box>
                          }
                        />
                        <FormControlLabel
                          value="custom"
                          control={<Radio />}
                          label={
                            <Box>
                              <Typography variant="body1">Tegn tilpasset boost-område</Typography>
                              <Typography variant="caption" color="text.secondary">
                                Boost gælder kun i et specifikt område (skal være indenfor serviceområdet)
                              </Typography>
                            </Box>
                          }
                        />
                      </RadioGroup>

                      {/* Show map when custom boost area is selected */}
                      {(hasBoostAreaEnabled(formData.boost_area) || !formData.service_area) ? (
                        <Box>
                          <Alert severity="info" sx={{ mb: 2 }}>
                            <strong>Tegn boost-område:</strong> Tegn det område hvor boost skal gælde. 
                            {formData.service_area && ' Serviceområdet vises som reference i lyseblå.'}
                          </Alert>
                          
                          <MapDrawing
                            address={formData.address}
                            location={formData.location}
                            defaultRadius={formData.default_radius || 30}
                            serviceArea={serviceAreaToGeoJSON(formData.boost_area)}
                            backgroundArea={serviceAreaToGeoJSON(formData.service_area)}
                            onServiceAreaChange={(geoJSON) => {
                              const boostArea = geoJSON ? geoJSONToServiceArea(geoJSON) : null
                              setFormData({ ...formData, boost_area: boostArea })
                            }}
                          />
                          
                          {hasValidBoostArea(formData.boost_area) && (
                            <Button
                              variant="outlined"
                              color="warning"
                              onClick={() => setFormData({ ...formData, boost_area: null })}
                              sx={{ mt: 2 }}
                            >
                              Fjern tilpasset boost-område
                            </Button>
                          )}
                        </Box>
                      ) : (
                        <Alert severity="success" sx={{ mb: 2 }}>
                          ✓ Boost gælder i hele serviceområdet
                        </Alert>
                      )}
                    </>
                  )}

                  {!formData.global_boost && (
                    <Alert severity="info">
                      Aktivér global boost for at konfigurere boost-område.
                    </Alert>
                  )}
                </Box>
              </TabPanel>
            )}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/undertakers')}
            disabled={saving}
          >
            Annuller
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="success"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={saving}
          >
            {saving ? 'Gemmer...' : 'Gem bedemand'}
          </Button>
        </Box>
      </form>
    </Box>
  )
}

